import { getCustomerUserPage } from '@/services/ant-design-pro/api';
import type { CustomerUserVO } from '@/services/ant-design-pro/typings';
import { SearchOutlined } from '@ant-design/icons';
import { App, Button, Descriptions, Empty, Form, Input, Modal, Table } from 'antd';
import React, { useEffect, useState } from 'react';

interface CustomerUserQueryModalProps {
  visible: boolean;
  onCancel: () => void;
}

const displayText = (value?: string) => value?.trim() || '-';

const CustomerUserQueryModal: React.FC<CustomerUserQueryModalProps> = ({
  visible,
  onCancel,
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm<{ phone: string }>();
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [list, setList] = useState<CustomerUserVO[]>([]);

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setList([]);
      setSearched(false);
      setLoading(false);
    }
  }, [form, visible]);

  const handleSearch = async () => {
    try {
      const values = await form.validateFields();
      const phone = values.phone.replace(/\s/g, '');
      setLoading(true);
      const response = await getCustomerUserPage({
        phone,
        page: 1,
        pageSize: 50,
      });

      if (response.success) {
        setList(response.result?.list || []);
        setSearched(true);
        return;
      }

      setList([]);
      setSearched(true);
      message.error(response.message || '查询失败');
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      console.error('查询客户用户失败:', error);
      setList([]);
      setSearched(true);
      message.error('查询失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: '账号',
      dataIndex: 'username',
      width: 140,
      render: (value: string) => displayText(value),
    },
    {
      title: '姓名',
      dataIndex: 'cname',
      width: 120,
      render: (value: string) => displayText(value),
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      width: 140,
      render: (value: string) => displayText(value),
    },
    {
      title: '客户名称',
      dataIndex: 'customerName',
      ellipsis: true,
      render: (value: string) => displayText(value),
    },
    {
      title: '所属机构',
      dataIndex: 'organizationName',
      ellipsis: true,
      render: (value: string) => displayText(value),
    },
  ];

  return (
    <Modal
      title="客户用户查询"
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={760}
      destroyOnClose
      maskClosable={false}
    >
      <Form
        form={form}
        layout="inline"
        onFinish={handleSearch}
        style={{ marginBottom: 16 }}
      >
        <Form.Item
          name="phone"
          label="手机号"
          rules={[
            { required: true, whitespace: true, message: '请输入手机号' },
            { pattern: /^1\d{10}$/, message: '请输入11位手机号' },
          ]}
        >
          <Input
            placeholder="请输入手机号"
            allowClear
            maxLength={11}
            style={{ width: 240 }}
          />
        </Form.Item>
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            icon={<SearchOutlined />}
            loading={loading}
          >
            查询
          </Button>
        </Form.Item>
      </Form>

      {searched && list.length === 0 && (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="未查询到客户用户" />
      )}

      {searched && list.length === 1 && (
        <Descriptions column={1} bordered size="small">
          <Descriptions.Item label="账号">{displayText(list[0].username)}</Descriptions.Item>
          <Descriptions.Item label="姓名">{displayText(list[0].cname)}</Descriptions.Item>
          <Descriptions.Item label="手机号">{displayText(list[0].phone)}</Descriptions.Item>
          <Descriptions.Item label="客户名称">{displayText(list[0].customerName)}</Descriptions.Item>
          <Descriptions.Item label="所属机构">
            {displayText(list[0].organizationName)}
          </Descriptions.Item>
        </Descriptions>
      )}

      {searched && list.length > 1 && (
        <Table<CustomerUserVO>
          rowKey={(record) => String(record.id ?? `${record.username}-${record.customerId}`)}
          dataSource={list}
          columns={columns}
          pagination={false}
          size="small"
          scroll={{ x: 680 }}
        />
      )}
    </Modal>
  );
};

export default CustomerUserQueryModal;
